"use client";

import { useMemo } from "react";

function buildNiceChips(min, max, step, slots = 8) {
  const snap = (x) => {
    const k = Math.round((x - min) / step);
    const snapped = min + k * step;
    const clamped = Math.max(min, Math.min(max, snapped));
    return Number(clamped.toFixed(2));
  };

  const isOnGrid = (x) => {
    const k = (x - min) / step;
    return Math.abs(k - Math.round(k)) < 1e-6;
  };

  const targets = [min, 10, 25, 50, 75, 100, 150, 200, 250, 500, max];
  const set = new Set();

  for (const t of targets) {
    if (!Number.isFinite(t)) continue;
    const v = snap(t);
    if (isOnGrid(v)) set.add(v);
  }

  if (set.size < slots) {
    const gap = Math.max(step, (max - min) / (slots - 1));
    for (let i = 0; i < slots && set.size < slots; i++) {
      set.add(snap(min + i * gap));
    }
    set.add(snap(max));
  }

  set.add(snap(min));
  set.add(snap(max));

  const arr = Array.from(set).sort((a, b) => a - b);

  if (arr.length > slots) {
    const first = arr.slice(0, Math.max(0, slots - 1));
    const last = arr[arr.length - 1];
    const out = [...first];
    if (!out.includes(last)) out.push(last);
    return out;
  }

  return arr;
}

export default function AmountSelector({
  min,
  max,
  step = 1,
  priceCurrency = "USD",
  selectedAmount,
  setSelectedAmount,
  priceMinDisplay,
  priceMaxDisplay,
}) {
  const clamp = (v) => Math.max(min, Math.min(max, v));

  const snapToStep = (x) => {
    const n = Number(x);
    if (!Number.isFinite(n)) return min;
    const k = Math.round((n - min) / step);
    return Number((min + k * step).toFixed(2));
  };

  const count = Math.floor((max - min) / step) + 1;

  const options =
    count > 0 && count <= 40
      ? Array.from({ length: count }, (_, i) => min + i * step)
      : [];

  const chips = useMemo(
    () => buildNiceChips(min, max, step, 8),
    [min, max, step]
  );

  const moneyFmt = useMemo(
    () =>
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: priceCurrency || "USD",
        currencyDisplay: "symbol",
      }),
    [priceCurrency]
  );

  const fmt = (v) => moneyFmt.format(v);

  const dec = () =>
    setSelectedAmount((v) => clamp(snapToStep((Number(v) || min) - step)));

  const inc = () =>
    setSelectedAmount((v) => clamp(snapToStep((Number(v) || min) + step)));

  return (
    <div className="mb-3" onClick={(e) => e.stopPropagation()}>
      <label className="mb-1 block text-xs text-textPrimary">
        Choose amount ({priceCurrency})
      </label>

      {count <= 40 && (
        <select
          className="block w-full rounded-md border border-border bg-background/30 px-2 py-2 text-sm text-textPrimary focus:outline-none focus:ring-2 focus:ring-primary/30 sm:hidden"
          value={Number.isFinite(selectedAmount) ? selectedAmount : min}
          onChange={(e) => setSelectedAmount(clamp(Number(e.target.value)))}
        >
          {options.map((v) => (
            <option key={v} value={v}>
              {fmt(v)}
            </option>
          ))}
        </select>
      )}

      <div className={`${count <= 40 ? "hidden sm:flex" : "flex"} items-center gap-2`}>
        <button
          type="button"
          className="rounded-md border border-border bg-background/30 px-2 py-1 text-sm text-textPrimary hover:bg-background/50 focus:outline-none focus:ring-2 focus:ring-primary/30"
          onClick={dec}
          aria-label="Decrease amount"
        >
          −
        </button>

        <input
          type="text"
          inputMode="decimal"
          pattern="^[0-9]*[.,]?[0-9]*$"
          className="flex-1 rounded-md border border-border bg-background/30 px-2 py-1 text-sm text-textPrimary focus:outline-none focus:ring-2 focus:ring-primary/30"
          value={Number.isFinite(selectedAmount) ? String(selectedAmount) : String(min)}
          onChange={(e) => {
            const raw = e.target.value.replace(/[^\d.,]/g, "").replace(",", ".");
            const v = Number(raw);
            if (!Number.isFinite(v)) return;
            setSelectedAmount(clamp(snapToStep(v)));
          }}
        />

        <button
          type="button"
          className="rounded-md border border-border bg-background/30 px-2 py-1 text-sm text-textPrimary hover:bg-background/50 focus:outline-none focus:ring-2 focus:ring-primary/30"
          onClick={inc}
          aria-label="Increase amount"
        >
          +
        </button>
      </div>

      <div className="mt-2 flex w-full flex-wrap gap-1.5">
        {chips.map((v) => {
          const selected = Math.abs((Number(selectedAmount) || 0) - v) < 0.005;

          return (
            <button
              key={v}
              type="button"
              className={`rounded-full border px-2 py-1 text-[11px] transition focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                selected
                  ? "border-primary/60 bg-primary/10 text-textPrimary"
                  : "border-border bg-background/30 text-textPrimary hover:bg-background/50"
              }`}
              aria-pressed={selected}
              onClick={() => setSelectedAmount(v)}
            >
              {fmt(v)}
            </button>
          );
        })}

        <input
          type="range"
          min={min}
          max={max}
          step={5}
          value={Number.isFinite(selectedAmount) ? selectedAmount : min}
          onChange={(e) => {
            const raw = Number(e.target.value);
            setSelectedAmount(clamp(snapToStep(raw)));
          }}
          className="mt-2 w-full"
        />
      </div>

      <div className="mt-1 text-[10px] text-textPrimary">
        Range: {priceMinDisplay} – {priceMaxDisplay}
      </div>
    </div>
  );
}